import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';
import type { AcceptConfig, PaymentNonce } from './acceptjs';

/**
 * Per-sale registration (Track B Phase 1a).
 *
 * A logged-in user must explicitly opt in to a specific auction before they can
 * bid on it. Nobody is auto-registered. This is the registration "chassis" that
 * card-authorization (Authorize.net) will later bolt onto — registration and
 * card-auth are deliberately separate steps.
 *
 * Collection: saleRegistrations/{auctionId}_{uid}
 * Fields: auctionId, uid, email, name, registeredAt (server timestamp)
 *
 * NO payment logic lives here. This is pure opt-in bookkeeping.
 */

export interface SaleRegistration {
  auctionId: string;
  uid: string;
  email: string;
  name: string;
  registeredAt: unknown; // Firestore server timestamp
}

/** Deterministic doc id so a user has exactly one registration per auction. */
export const saleRegistrationId = (auctionId: string, uid: string): string =>
  `${auctionId}_${uid}`;

/**
 * Write the registration record for {auctionId, uid}. Idempotent by id, but the
 * Firestore rules only permit CREATE (no updates), so callers should register
 * once; the UI gates on the registered state to avoid a redundant write.
 */
export const registerForSale = async (
  auctionId: string,
  uid: string,
  email: string,
  name: string,
): Promise<void> => {
  const ref = doc(db, 'saleRegistrations', saleRegistrationId(auctionId, uid));
  await setDoc(ref, {
    auctionId,
    uid,
    email: (email || '').trim(),
    name: (name || '').trim(),
    registeredAt: serverTimestamp(),
  });
};

/** True if this user has already registered for this auction. */
export const isRegisteredForSale = async (
  auctionId: string,
  uid: string,
): Promise<boolean> => {
  const ref = doc(db, 'saleRegistrations', saleRegistrationId(auctionId, uid));
  const snap = await getDoc(ref);
  return snap.exists();
};

// ---------------------------------------------------------------------------
// Track B Phase 1b — card-authorized registration via Cloud Functions.
//
// The callable `registerForSale` gates registration on a $0 Authorize.net card
// authorization (SANDBOX). It returns 'registered' when done, or signals
// 'card_required' (a failed-precondition) when the caller needs to present the
// Accept.js card form and retry with a payment nonce. Card verification is
// cached server-side for 6 months, so returning bidders skip the card step.
// ---------------------------------------------------------------------------

const registerFn = httpsCallable(functions, 'registerForSale');
const acceptConfigFn = httpsCallable(functions, 'getAcceptJsConfig');

/** Fetch the public Accept.js config (API Login ID + Public Client Key). */
export const fetchAcceptJsConfig = async (): Promise<AcceptConfig> => {
  const res = await acceptConfigFn({});
  const data = res.data as { apiLoginId: string; clientKey: string };
  return { apiLoginId: data.apiLoginId, clientKey: data.clientKey };
};

export type RegisterOutcome = 'registered' | 'card_required';

/**
 * Register the current user for a sale via the Cloud Function. Pass a nonce
 * once the Accept.js card form has produced one. Returns 'card_required' if the
 * server needs a card before it will register (so the caller can show the form).
 */
export const submitSaleRegistration = async (
  auctionId: string,
  opaqueData?: PaymentNonce,
): Promise<RegisterOutcome> => {
  try {
    await registerFn({ auctionId, opaqueData });
    return 'registered';
  } catch (e: any) {
    // The function throws failed-precondition('card_required') when a fresh
    // card token is missing. firebase/functions surfaces it as error.message.
    if (e?.message === 'card_required' || /card_required/.test(String(e?.message))) {
      return 'card_required';
    }
    throw e;
  }
};
